import { useTranslation } from 'react-i18next';
import esES from 'antd/es/date-picker/locale/es_ES';
import enUS from 'antd/es/date-picker/locale/en_US';
import skSK from 'antd/es/date-picker/locale/sk_SK';
import { DEFAULT_APP_LANGUAGE, resolveAppLanguage } from '../constants/appLanguages';

/**
 * The locale to hand antd's DatePicker/RangePicker.
 *
 * The app never sets antd's own `ConfigProvider` locale -- it drives its own
 * text through i18next -- so antd's built-in month names and "Now"/"Ok"
 * footer default to English whatever language the app is in. Passing a locale
 * per picker fixes that without a global change that would also reword every
 * other antd component (Popconfirm, Table, Pagination...).
 *
 * This was written inline inside HomeSearchBar, including the "Ok" -> "Aplicar"
 * override, which is the sort of thing that gets copy-pasted the second time
 * somebody needs a date picker and then drifts. It is one hook now.
 *
 * One entry per language in APP_LANGUAGES -- a language added there without
 * an entry here falls back to English month names while the rest of the
 * screen is translated, which reads as a bug rather than as a gap.
 */
const PICKER_LOCALE_BY_LANGUAGE: Record<string, typeof enUS> = {
  en: enUS,
  es: esES,
  sk: skSK,
};

/**
 * antd's own label is "Ok", which reads as a dismissal rather than as
 * "apply this range".
 */
const OK_LABEL_BY_LANGUAGE: Record<string, string> = {
  en: 'Apply',
  es: 'Aplicar',
  sk: 'Použiť',
};

export const useAntdPickerLocale = () => {
  const { i18n } = useTranslation();
  const language = resolveAppLanguage(i18n.resolvedLanguage ?? i18n.language);
  const base = PICKER_LOCALE_BY_LANGUAGE[language] ?? PICKER_LOCALE_BY_LANGUAGE[DEFAULT_APP_LANGUAGE];

  return {
    ...base,
    lang: { ...base.lang, ok: OK_LABEL_BY_LANGUAGE[language] ?? OK_LABEL_BY_LANGUAGE[DEFAULT_APP_LANGUAGE] },
  };
};
